import { useMutation } from '@tanstack/react-query';

import { API, DOMAIN } from '@autopass-web/domain';

import Client from './client';

import type { UseMutationOptions } from '@tanstack/react-query';

type MutationFnProps = {
  cardNumber: string;
  cardExpireDate: string;
  cardCvc: string;
  callbackUrl: string;
};

const formAction = `${DOMAIN.API_URL[API.PAYMENT].baseUrl}/credit_card`;

export const useCreditCardValidate = (
  options: Omit<
    UseMutationOptions<void, Error, MutationFnProps>,
    'mutationFn'
  > = {},
) => {
  const mutation = useMutation({
    mutationFn: async ({
      cardNumber,
      cardExpireDate,
      cardCvc,
      callbackUrl,
    }: MutationFnProps) => {
      try {
        // Init mochipay validation to get merchant_uid and transaction_id
        const { data } = await Client.initMochipayValidation();
        const { merchant_uid, transaction_id } = data.data;

        // Create a form and attach it to document to submit the credit card information later
        const form = create3DValidationForm({
          formData: {
            cardNumber,
            cardExpireDate,
            cardCvc,
            merchantUid: merchant_uid,
            transactionId: transaction_id,
            callbackUrl,
          },
          formConfig: {
            action: formAction,
            method: 'POST',
          },
        });

        // Submit the form
        form.submit();
      } catch (err) {
        console.log(err);
        throw err;
      }
    },
    ...options,
  });

  return mutation;
};

type CreditCardFormConfig = {
  action: string;
  method: 'POST' | 'GET' | 'DELETE' | 'PUT';
};

type CreditCardFormData = {
  cardNumber: string;
  cardExpireDate: string;
  cardCvc: string;
  merchantUid: string;
  transactionId: string;
  callbackUrl: string;
  orderType?: string;
  transactionType?: string;
  amount?: string;
};

interface CreateCreditCardSessionProps {
  formConfig: CreditCardFormConfig;
  formData: CreditCardFormData;
}

function create3DValidationForm({
  formConfig,
  formData,
}: CreateCreditCardSessionProps) {
  const {
    cardNumber,
    cardExpireDate,
    cardCvc,
    merchantUid,
    transactionId,
    callbackUrl,
    orderType,
    transactionType,
    amount,
  } = formData;
  const form = document.createElement('form');

  form.method = formConfig.method;
  form.action = formConfig.action;
  form.setAttribute('hidden', 'true');

  const customDataInputField = document.createElement('input');
  customDataInputField.name = 'custom_data[is_default]';
  customDataInputField.value = 'yes';
  customDataInputField.type = 'hidden';

  const inputGroup = [
    {
      name: 'pan',
      value: cardNumber.replace(/\s/g, ''),
    },
    {
      name: 'expire_date',
      value: cardExpireDate.replace(/\//g, ''),
    },
    {
      name: 'cvc',
      value: cardCvc,
    },
    {
      name: 'merchant_uid',
      value: merchantUid,
    },
    {
      name: 'transaction_id',
      value: transactionId,
    },
    {
      name: 'frontend_callback_url',
      value: callbackUrl,
    },
    // Optional fields
    {
      name: 'transaction_type',
      value: transactionType,
    },
    {
      name: 'order_type',
      value: orderType,
    },
    {
      name: 'amount',
      value: amount,
    },
  ];

  inputGroup.forEach(({ value, name }) => {
    if (value == null) return;

    const input = document.createElement('input');
    input.value = value;
    input.name = name;
    form.appendChild(input);
  });

  document.body.appendChild(form);

  return form;
}
